/* ──────────────────────────────────────────────────────────────
   ResultsPage.jsx  –  list icons stay white, detail icons coloured
   ────────────────────────────────────────────────────────────── */
import React, { useMemo, useState, useRef } from 'react';

/* asset map --------------------------------------------------- */
import hrIcon   from '../assets/HeartRate.png';
import brIcon   from '../assets/RespiratoryRate.png';
import spo2Icon from '../assets/OxygenLevel.png';
import bpIcon   from '../assets/BloodPressure.png';
import bsIcon   from '../assets/BloodSugur.png';
import cholIcon from '../assets/Cholesterol.png';

import { FaUserCircle }      from 'react-icons/fa';
import { useVitals }         from '../contexts/VitalProvider';
import DocsunVitalsHelper    from '../api/DocsunVitalsHelper';
import { statusFromPercent } from '../api/status';

import ResultRow    from './results/ResultRow';
import MetricDetail from './home/MetricDetail';

import logoPng from '../assets/truscan_logo.png';
import './results/ResultsPage.scss';

const fmt = (v, u = '') => (v === null || v === undefined ? '—' : `${v}${u}`);
const pct = DocsunVitalsHelper.convertRating;

/* ------------------------------------------------------------------ */
export default function ResultsPage() {
  const v = useVitals();
  const [selected, setSel] = useState(null);
  const [tab, setTab]      = useState('vitals');
  const scrollRef          = useRef(null);

  /* ---- build list rows ------------------------------------------ */
  const rows = useMemo(() => {
    const bpTxt =
      v.systolic && v.diastolic ? `${v.systolic}/${v.diastolic}` : null;

    /* helper to DRY up */
    const row = (id, label, src, value, score) => ({
      id,
      label,
      /* 1️⃣  coloured icon kept separately… */
      iconSrc : src,
      /* 2️⃣  white list-icon (CSS class) */
      icon    : <img src={src} alt={label} className="vitalIcons" />,
      value,
      percent : pct(score ?? 5),
    });

    return [
      row(1, 'Heart Rate',      hrIcon,   fmt(v.heartRate, ''),             v.hr_score),
      row(2, 'Breathing Rate',  brIcon,   fmt(v.respRate,  ''),             v.br_score),
      row(3, 'SpO₂',            spo2Icon, fmt(v.spo2,      ''),               v.spo2_score),
      row(4, 'Blood Pressure',  bpIcon,   bpTxt ?? '—',                         v.bp_score),
      row(5, 'Blood Sugar',     bsIcon,   fmt(v.bloodSugar
                                       ? Math.round(v.bloodSugar,1) : null,
                                       ''), v.bs_score),
      row(6, 'Cholesterol',     cholIcon, fmt(v.cholesterol
                                       ? Math.round(v.cholesterol * 38.67) : null,
                                       ''), v.chol_score),
    ].map(r => ({ ...r, status: statusFromPercent(r.percent) }));
  }, [v]);

  /* vitamins / wellness placeholders */
  const vitamins = [], wellness = [];

  const dataSet = tab === 'vitals' ? rows :
                  tab === 'vitamins' ? vitamins : wellness;

  /* ---- handlers -------------------------------------------------- */
  const openDetail = (r) => {
    /* clone row & swap in COLOURED icon (no white filter class) */
    setSel({
      ...r,
      icon: <img src={r.iconSrc} alt={r.label} />,   // ← no className
    });
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const timeLabel = v.updatedAt
    ? new Date(v.updatedAt).toLocaleString()
    : 'no scan yet';

  /* ---- render ---------------------------------------------------- */
  return (
    <div className="results-page" ref={scrollRef}>
      {/* top bar */}
      <header className="top-bar">
        <img src={logoPng} alt="logo" className="logo" />
        <FaUserCircle className="avatar" />
      </header>

      {/* inner tabs */}
      <div className="tabs-scroll">
        {['vitals', 'vitamins', 'wellness'].map(t => (
          <button
            key={t}
            type="button"
            className={`tab ${tab === t ? 'active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t[0].toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <div className="timestamp"><span>{timeLabel}</span></div>

      <section className="list">
        {dataSet.length === 0 ? (
          <div className="coming-soon">
            <h3>Coming&nbsp;Soon</h3>
            <p>Insights will appear here in a future update.</p>
          </div>
        ) : (
          dataSet.map(r => (
            <ResultRow key={r.id} data={r} onClick={openDetail} />
          ))
        )}
      </section>

      {selected && (
        <MetricDetail data={selected} onClose={() => setSel(null)} />
      )}
    </div>
  );
}
